import type {
  QuestionnaireGroupedMetrics,
  QuestionnaireMetric,
  QuestionnaireOptionRow,
  QuestionnaireReportTableVariant,
} from '../../components';

export interface QuestionnaireReportSection {
  key: string;
  variant: QuestionnaireReportTableVariant;
  title: string;
  rows: QuestionnaireOptionRow[];
  total?: QuestionnaireGroupedMetrics;
  subSections?: QuestionnaireReportSection[];
  /**
   * Message shown in the table body when the section has loaded with no rows
   * (e.g. the province section when no province matches the selected filters).
   * Only sections whose option set is data-driven set this — see
   * `buildProvinceSection`.
   */
  emptyMessage?: string;
}

/**
 * Structural shapes matched by every generated `*QuestionnaireReportMetrics`
 * / `*QuestionnaireReportRow` / `*QuestionnaireReportSection` type (Short,
 * Standard, ...). Keeping these structural — rather than importing one
 * domain's nominal generated type — lets the shared mapping helpers below
 * accept any of the generated report shapes without a cast at the call site.
 */
export interface QuestionnaireReportMetricsLike {
  count: number;
  pass: number;
  fail: number;
  passingRate: number;
  avgScore: number;
}

export interface QuestionnaireReportRowLike {
  optionKey: string;
  publicPlusInHouse: QuestionnaireReportMetricsLike;
  public: QuestionnaireReportMetricsLike;
  inHouse: QuestionnaireReportMetricsLike;
}

export interface QuestionnaireReportSectionLike {
  rows: QuestionnaireReportRowLike[];
  total: QuestionnaireReportRowLike;
}

export const AGE_LABELS: Record<string, string> = {
  '<25': '< 25 ปี',
  '25-30': '25-30 ปี',
  '30-35': 'มากกว่า 30-35 ปี',
  '35-40': 'มากกว่า 35-40 ปี',
  '40-45': 'มากกว่า 40-45 ปี',
  '45-50': 'มากกว่า 45-50 ปี',
  '>50': 'มากกว่า 50 ปี',
};

export const EDUCATION_LABELS: Record<string, string> = {
  'below-bachelor-degree': 'ต่ำกว่าปริญญาตรี (ม.6 / ปวช. / ปวส.)',
  'currently-in-bachelor-degree': 'กำลังศึกษาระดับปริญญาตรี',
  'bachelor-degree': 'จบการศึกษาระดับปริญญาตรี',
  'master-degree-or-higher': 'กำลังศึกษา หรือ จบการศึกษาระดับปริญญาโท ขึ้นไป',
  other: 'อื่น ๆ โปรดระบุ',
};

export const ORGANIZATION_LABELS: Record<string, string> = {
  studying: 'กำลังศึกษา (ไม่ได้ทำงาน)',
  'securities-company': 'บริษัทหลักทรัพย์ (รวมถึง บลน., บลป.)',
  'asset-management-company': 'บริษัทหลักทรัพย์จัดการกองทุน',
  bank: 'ธนาคาร',
  'insurance-company': 'บริษัทประกัน',
  government: 'หน่วยงานราชการ / รัฐวิสาหกิจ',
  'private-organization': 'องค์กรภาคเอกชน',
  freelance: 'ธุรกิจส่วนตัว / อาชีพอิสระ',
  other: 'อื่น ๆ โปรดระบุ',
};

export const RATING_LABELS: Record<string, string> = {
  'very-good': 'ดีมาก',
  good: 'ดี',
  fair: 'พอใช้',
  'need-improvement': 'ต้องปรับปรุง',
};

export function mapMetrics(metrics: QuestionnaireReportMetricsLike): QuestionnaireMetric {
  const { count, pass, fail, passingRate, avgScore } = metrics;
  return { count, pass, fail, passingRate, avgScore };
}

export function mapGrouped(row: QuestionnaireReportRowLike): QuestionnaireGroupedMetrics {
  return {
    combined: mapMetrics(row.publicPlusInHouse),
    public: mapMetrics(row.public),
    inHouse: mapMetrics(row.inHouse),
  };
}

export const EMPTY_METRIC: QuestionnaireMetric = {
  count: 0,
  pass: 0,
  fail: 0,
  passingRate: 0,
  avgScore: 0,
};

export const EMPTY_GROUPED_METRICS: QuestionnaireGroupedMetrics = {
  combined: EMPTY_METRIC,
  public: EMPTY_METRIC,
  inHouse: EMPTY_METRIC,
};

export interface MapSectionOptions {
  key: string;
  title: string;
  variant: QuestionnaireReportTableVariant;
}

export function mapSection(
  apiSection: QuestionnaireReportSectionLike,
  labelMap: Record<string, string>,
  { key, title, variant }: MapSectionOptions,
  /**
   * When true, the section renders exactly the options in `labelMap` (in that
   * order), joining API metrics by `optionKey` and zero-filling any option the
   * API didn't return. Any API row whose `optionKey` is NOT in `labelMap` is
   * dropped. Use this when the report must show only a fixed, known option set
   * regardless of what the aggregation returns (e.g. the Standard template,
   * whose backend buckets some questions by a wider taxonomy). When false (the
   * default), every API row is shown, falling back to the raw `optionKey` for
   * unmapped keys.
   */
  restrictToKnownOptions = false,
): QuestionnaireReportSection {
  const rows = restrictToKnownOptions
    ? Object.keys(labelMap).map((optionKey, i) => {
        const apiRow = apiSection.rows.find((r) => r.optionKey === optionKey);
        return {
          id: `${key}-${i + 1}`,
          label: labelMap[optionKey],
          metrics: apiRow ? mapGrouped(apiRow) : EMPTY_GROUPED_METRICS,
        };
      })
    : apiSection.rows.map((r, i) => ({
        id: `${key}-${i + 1}`,
        label: labelMap[r.optionKey] ?? r.optionKey,
        metrics: mapGrouped(r),
      }));

  return { key, variant, title, rows, total: mapGrouped(apiSection.total) };
}

/**
 * Placeholder section built from the known option labels, used while the
 * query is in flight (`report` not yet available). It keeps the table
 * scaffold on screen — every option row + Total is present — so the
 * `loading` skeleton renders in place instead of the table disappearing.
 */
export function placeholderSection(
  labelMap: Record<string, string>,
  { key, title, variant }: MapSectionOptions,
): QuestionnaireReportSection {
  return {
    key,
    variant,
    title,
    rows: Object.entries(labelMap).map(([, label], i) => ({
      id: `${key}-${i + 1}`,
      label,
      metrics: EMPTY_GROUPED_METRICS,
    })),
    total: EMPTY_GROUPED_METRICS,
  };
}

export interface SectionConfig<TField extends string = string> extends MapSectionOptions {
  field: TField;
  labelMap: Record<string, string>;
  /** See `mapSection`'s `restrictToKnownOptions`. Defaults to false. */
  restrictToKnownOptions?: boolean;
}

export function buildSection<TReport, TField extends keyof TReport & string>(
  config: SectionConfig<TField>,
  report: TReport | undefined,
): QuestionnaireReportSection {
  const { field, labelMap, restrictToKnownOptions, ...options } = config;
  if (!report) {
    return placeholderSection(labelMap, options);
  }
  const apiSection = report[field] as unknown as QuestionnaireReportSectionLike;
  return mapSection(apiSection, labelMap, options, restrictToKnownOptions);
}
